import React from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import { NEUTRAL, RADIUS } from "../styles/brandTokens";
import type { AutomationCapacity } from "../types/integration";

export interface AgentCapacityOption {
  value: AutomationCapacity;
  label: string;
  read: string;
  write: string;
}

/**
 * Read is lookup. Write is a change to a customer system.
 * Suggestions posts a note and stops; it does not run those changes.
 * Auto Resolver runs them, and an action set to approval still waits.
 */
export const CAPACITY_OPTIONS: AgentCapacityOption[] = [
  {
    value: 3,
    label: "Auto Resolver",
    read: "Looks up the ticket, the user, devices, and connected apps on its own.",
    write: "Makes the change. An action set to approval still waits for a technician.",
  },
  {
    value: 1,
    label: "Suggestions",
    read: "Reads the ticket and drafts the next actions.",
    write: "Does not change accounts, devices, or settings. It posts the suggestions for a technician.",
  },
  {
    value: 0,
    label: "Off",
    read: "Does not look anything up.",
    write: "Does not change anything.",
  },
];

export function capacityOption(value: AutomationCapacity): AgentCapacityOption {
  return CAPACITY_OPTIONS.find((option) => option.value === value) ?? CAPACITY_OPTIONS[2];
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${RADIUS.control}px`,
    backgroundColor: NEUTRAL.surface,
    fontSize: 14,
    "& fieldset": { borderColor: NEUTRAL.border },
    "&:hover fieldset": { borderColor: NEUTRAL.borderStrong },
  },
} as const;

const ReadWrite: React.FC<{ option: AgentCapacityOption; compact?: boolean }> = ({ option, compact = false }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "52px 1fr",
      rowGap: compact ? 0.25 : 0.5,
      columnGap: 1,
    }}
  >
    {(
      [
        ["Read", option.read],
        ["Write", option.write],
      ] as const
    ).map(([name, detail]) => (
      <React.Fragment key={name}>
        <Typography sx={{ fontSize: compact ? 12 : 12.5, fontWeight: 700, color: NEUTRAL.text }}>{name}</Typography>
        <Typography sx={{ fontSize: compact ? 12 : 12.5, lineHeight: 1.45, color: NEUTRAL.textSecondary }}>{detail}</Typography>
      </React.Fragment>
    ))}
  </Box>
);

/** The configure screen only offers Auto Resolver. Read and write stay visible without a menu. */
export const AutoResolverSummary: React.FC = () => {
  const option = capacityOption(3);
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: `${RADIUS.control}px`,
        border: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.subtle,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: NEUTRAL.text, mb: 0.75 }}>{option.label}</Typography>
      <ReadWrite option={option} />
    </Box>
  );
};

export const AutomationLevelSelect: React.FC<{
  value: AutomationCapacity;
  onChange: (value: AutomationCapacity) => void;
}> = ({ value, onChange }) => {
  const selected = capacityOption(value);
  return (
    <>
      <Select
        id="agent-capacity"
        fullWidth
        size="small"
        value={value}
        onChange={(event) => onChange(Number(event.target.value) as AutomationCapacity)}
        renderValue={() => selected.label}
        MenuProps={{ PaperProps: { sx: { maxWidth: 520 } } }}
        sx={{
          ...fieldSx,
          borderRadius: `${RADIUS.control}px`,
          fontSize: 14,
          backgroundColor: NEUTRAL.surface,
        }}
      >
        {CAPACITY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{ fontSize: 14, alignItems: "flex-start", whiteSpace: "normal", py: 1.25 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: NEUTRAL.text }}>{option.label}</Typography>
              <Box sx={{ mt: 0.5 }}>
                <ReadWrite option={option} compact />
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      <Box
        sx={{
          mt: 1.25,
          px: 1.5,
          py: 1.25,
          borderRadius: `${RADIUS.control}px`,
          border: `1px solid ${NEUTRAL.border}`,
          backgroundColor: NEUTRAL.subtle,
        }}
      >
        <ReadWrite option={selected} />
      </Box>
    </>
  );
};
